import { useCallback, useEffect, useMemo, useState } from 'react'
import feedbackRequest from 'src/api/requests/feedback'
import selectedPlanRequest from 'src/api/requests/selectedFromLp'
import { MESSAGE_TYPE } from 'src/containers/Chat/constants'
import { PlanDataInterface } from 'src/interfaces/plan'

export const usePlanMessageData = (
  type_message: number,
  message_content: string,
) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanDataInterface | null>(
    null,
  )
  const [feedbackData, setFeedbackData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (type_message === MESSAGE_TYPE.PLAN && message_content) {
      try {
        const data = JSON.parse(message_content)
        const feedbackId = data?.doctor_feedback_id
        const treatmentId = data?.user_treatment_id

        if (!feedbackId && !treatmentId) return

        setIsLoading(true)

        const promises: Promise<any>[] = []

        if (feedbackId) {
          promises.push(
            feedbackRequest
              .getFeedbackDetail(feedbackId, { with: 'plans.medicines,doctor' })
              .then((res) => setFeedbackData(res.data)),
          )
        }

        if (treatmentId) {
          promises.push(
            selectedPlanRequest
              .getSelectedPlan({
                user_treatment_id: treatmentId,
                with: ['plan.medicines:id,name,usage'],
              } as any)
              .then((res) => setSelectedPlan(res.data.data)),
          )
        }

        Promise.all(promises).finally(() => setIsLoading(false))
      } catch {
        return
      }
    }
  }, [type_message, message_content])

  useEffect(() => {
    if (feedbackData?.plans?.length) {
      const recommended = feedbackData.plans.find(
        (plan) => plan.pivot?.recommended === true,
      )
      if (recommended && !selectedPlan) {
        setSelectedPlan(recommended)
      }
    }
  }, [feedbackData, selectedPlan])

  const mapPlanData = useCallback(
    (plan: any): PlanDataInterface => ({
      ...plan,
      medicines: plan.medicines || [],
      tags: plan.tags || [],
      treatment_policy: plan.treatment_policy || '',
    }),
    [],
  )

  const planImportants = useMemo(() => {
    const importantFromFeedbacks =
      feedbackData?.plans?.filter((plan) => plan.pivot?.recommended) ?? []

    const allImportant = [...importantFromFeedbacks]

    if (
      selectedPlan?.recommended &&
      !allImportant.some((p) => p.id === selectedPlan.id)
    ) {
      allImportant.push(selectedPlan)
    }

    return allImportant.map(mapPlanData)
  }, [feedbackData, selectedPlan])

  const planContents = useMemo(() => {
    const fbPlans = feedbackData?.plans?.map(mapPlanData) || []
    if (!selectedPlan) return fbPlans

    const exists = fbPlans.some((p) => p.id === selectedPlan.id)
    const merged = exists ? fbPlans : [...fbPlans, mapPlanData(selectedPlan)]

    return merged.filter(
      (plan) => !planImportants.some((imp) => imp.id === plan.id),
    )
  }, [feedbackData, selectedPlan, planImportants])

  return { planContents, planImportants, selectedPlan, feedbackData, isLoading }
}
